import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/servicesTabStyle';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { HeadingSBold, BodySBold } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { translate } from 'components/Translation';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import SubExpansionPanelWrapper from './subcomponents/SubExpansionPanelWrapper';
import { ISelectedPackage } from 'views/PrepaidLoyalty/AddNewServices';
import classNames from 'classnames';

// @ts-ignore
const useStyles = makeStyles(styles);

type IMainExpansionPanelProps = {
	title: string;
	selectedPackages: ISelectedPackage[];
	collapsedMain: string;
	collapsedSub: string;
	handleCollapsedMain: (name: string) => void;
	handleCollapsedSub: (name: string) => void;
	servicePackages: {
		type: types.ecommerce.PackageType;
		groupBy?: types.topUpAddOns.IGroupBy;
	}[];
	handleSelected: (singlePackage: ISelectedPackage) => void;
	setPackagesLoading: React.Dispatch<React.SetStateAction<{ type: types.ecommerce.PackageType; loading: boolean }[]>>;
};

const MainExpansionPanel = ({
	title,
	selectedPackages,
	collapsedMain,
	handleCollapsedMain,
	servicePackages,
	collapsedSub,
	handleCollapsedSub,
	handleSelected,
	setPackagesLoading,
}: IMainExpansionPanelProps) => {
	const classes = useStyles();
	const [sumOfChildPackages, setSumOfChildPackages] = useState<number>(0);

	const isCollapsed = useMemo(() => collapsedMain === title, [collapsedMain, title]);

	const filteredSelectedPackagesCount: number = useMemo(() => {
		const types = servicePackages.map(({ type }) => type);
		const groups = servicePackages
			.map(({ groupBy }) => groupBy?.values)
			.filter(Boolean)
			.flat();
		return selectedPackages.filter(({ type }) => types.indexOf(type) >= 0 || groups.indexOf(type) >= 0).length;
	}, [selectedPackages, servicePackages]);

	return (
		<ConditionalRender
			show={servicePackages.length > 1}
			onTrue={() => (
				<div className={classNames({ [classes.expansionPanelHidden]: sumOfChildPackages <= 0 })}>
					<ExpansionPanel
						expanded={isCollapsed}
						onChange={() => handleCollapsedMain(title)}
						classes={{
							root: classes.expansionPanel,
							expanded: classes.expansionPanelExpanded,
						}}
					>
						<ExpansionPanelSummary
							expandIcon={<ExpandMore />}
							classes={{
								root: classes.expansionPanelSummary,
								expanded: classes.expansionPanelSummaryExpanded,
								content: classes.expansionPanelSummaryContent,
								expandIcon: classes.expansionPanelSummaryExpandIcon,
							}}
						>
							<HeadingSBold disableMargin>{title}</HeadingSBold>
							<ConditionalRender
								show={!isCollapsed && filteredSelectedPackagesCount > 0}
								onTrue={() => (
									<div className={classes.selectedPackCount}>
										<div className={classes.badge}></div>
										<BodySBold disableMargin inline color="enterprise">
											{translate(
												'prepaid.loyalty.add.new.services.services.amount',
												String(filteredSelectedPackagesCount)
											)}
										</BodySBold>
									</div>
								)}
							/>
						</ExpansionPanelSummary>
					</ExpansionPanel>
				</div>
			)}
			onFalse={() => (
				<ExpansionPanelDetails className={classes.expansionPanelDetails}>
					<div className={classes.expansionSubpanelContainer}>
						{servicePackages.map((singleServicePackage) => (
							<SubExpansionPanelWrapper
								setPackagesLoading={setPackagesLoading}
								collapsedSub={collapsedSub}
								handleSelected={handleSelected}
								handleCollapsedSub={handleCollapsedSub}
								servicesPackage={singleServicePackage}
								key={singleServicePackage.type}
								selectedPackages={selectedPackages}
								setSumOfChildPackages={setSumOfChildPackages}
							/>
						))}
					</div>
				</ExpansionPanelDetails>
			)}
		/>
	);
};
export default MainExpansionPanel;
